import { jsPDF } from 'jspdf';
import { drawPdfHeader } from './pdf-branding.util';
import {
  IbnProtokoll,
  IbnProtokollFeld,
  IbnProtokollInbetriebnahme,
  IbnProtokollMessstelleDetail,
  IbnProtokollMessstelleZeile,
  IbnProtokollPruefpunkt,
  IbnProtokollStatistik,
  IbnProtokollUmfang
} from '../../../Models/ibn-protokoll.model';
import { MessstellePruefprotokoll, formatAktivitaet } from '../../../Models/messstelle.model';

/**
 * PDF-Aufbereitung des IBN-Protokolls – für ein Projekt, eine Inbetriebnahme
 * oder eine einzelne Messstelle.
 *
 * Die Farbwelt und der Kopf sind bewusst dieselben wie beim Einsatz-/Wartungs-
 * bericht (siehe service-report-pdf.util.ts), damit alle Dokumente des Systems
 * gleich aussehen. Die Auswertung selbst kommt fertig vom Server; hier wird
 * nichts mehr gerechnet außer Layout.
 */

// Abgestimmt auf den ELIN-Header.
const COLOR = {
  primary: [0, 95, 155] as const,
  ink: [26, 26, 46] as const,
  muted: [95, 104, 118] as const,
  faint: [150, 158, 168] as const,
  line: [214, 222, 232] as const,
  bgSoft: [244, 247, 252] as const,
  zebra: [249, 251, 254] as const,
  ok: [46, 125, 50] as const,
  notOk: [211, 47, 47] as const,
  open: [176, 132, 0] as const,
  neutral: [95, 104, 118] as const
};

const M = 14;                 // Seitenrand
const FOOTER_SPACE = 16;      // Platz für die Fußzeile
const TOP_ON_NEW_PAGE = 18;   // Startpunkt nach einem Seitenumbruch

const DOKUMENT_TITEL = 'IBN-Protokoll';

// ── Grundlagen ──────────────────────────────────────────────────────────────

function pageWidth(doc: jsPDF): number {
  return doc.internal.pageSize.getWidth();
}

function pageHeight(doc: jsPDF): number {
  return doc.internal.pageSize.getHeight();
}

function contentWidth(doc: jsPDF): number {
  return pageWidth(doc) - 2 * M;
}

function bottomLimit(doc: jsPDF): number {
  return pageHeight(doc) - FOOTER_SPACE;
}

/** Sorgt für genügend Platz; bricht sonst auf eine neue Seite um. */
function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > bottomLimit(doc)) {
    doc.addPage();
    return TOP_ON_NEW_PAGE;
  }
  return y;
}

function setText(doc: jsPDF, color: readonly number[]): void {
  doc.setTextColor(color[0], color[1], color[2]);
}

/** Abschnittstitel mit blauem Akzentbalken und feiner Linie. */
function sectionTitle(doc: jsPDF, y: number, title: string, hinweis?: string): number {
  y = ensureSpace(doc, y, 20);
  y += 3;

  doc.setFillColor(...COLOR.primary);
  doc.rect(M, y - 3.6, 1.6, 5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11.5);
  setText(doc, COLOR.ink);
  doc.text(title, M + 4.5, y);

  if (hinweis) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    setText(doc, COLOR.muted);
    doc.text(hinweis, pageWidth(doc) - M, y, { align: 'right' });
  }

  doc.setDrawColor(...COLOR.line);
  doc.setLineWidth(0.25);
  doc.line(M, y + 2.2, pageWidth(doc) - M, y + 2.2);
  return y + 8;
}

/** Untertitel innerhalb eines Abschnitts (z. B. eine einzelne Inbetriebnahme). */
function subTitle(doc: jsPDF, y: number, title: string, rechts?: string): number {
  const cw = contentWidth(doc);
  y = ensureSpace(doc, y, 12);

  doc.setFillColor(...COLOR.bgSoft);
  doc.roundedRect(M, y, cw, 7, 1.5, 1.5, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  setText(doc, COLOR.ink);
  doc.text(kuerzen(doc, title, cw - 70), M + 3, y + 4.8);

  if (rechts) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    setText(doc, COLOR.muted);
    doc.text(rechts, M + cw - 3, y + 4.8, { align: 'right' });
  }

  return y + 10;
}

/** Mehrzeiliger Fließtext; bricht zeilenweise um. */
function paragraph(doc: jsPDF, y: number, text: string, options?: {
  x?: number; width?: number; size?: number; color?: readonly number[]; style?: string;
}): number {
  const x = options?.x ?? M;
  const width = options?.width ?? (pageWidth(doc) - M - x);
  const size = options?.size ?? 9.5;
  const lineHeight = size * 0.46;

  doc.setFont('helvetica', options?.style ?? 'normal');
  doc.setFontSize(size);
  setText(doc, options?.color ?? COLOR.ink);

  for (const line of doc.splitTextToSize(text, width) as string[]) {
    y = ensureSpace(doc, y, lineHeight);
    doc.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

/** Einzeiliger Text, notfalls mit „…“ gekürzt. */
function kuerzen(doc: jsPDF, text: string, width: number): string {
  const zeilen = doc.splitTextToSize(text ?? '', width) as string[];
  if (zeilen.length <= 1) {
    return zeilen[0] ?? '';
  }
  return (doc.splitTextToSize(text, width - 4)[0] ?? '') + ' …';
}

function datum(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-AT');
}

function datumZeit(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString('de-AT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
}

function text(value?: string | null): string {
  return (value ?? '').trim();
}

// ── Bausteine ───────────────────────────────────────────────────────────────

interface InfoEintrag {
  label: string;
  value: string;
}

/** Allgemeine Angaben als zweispaltiges Raster in einer hellen Box. */
function infoGrid(doc: jsPDF, y: number, entries: InfoEintrag[]): number {
  const gefuellt = entries.filter(e => text(e.value).length > 0);
  if (gefuellt.length === 0) return y;

  const cw = contentWidth(doc);
  const colW = cw / 2;
  const rowH = 9.5;
  const rows = Math.ceil(gefuellt.length / 2);
  const boxH = rows * rowH + 6;

  y = ensureSpace(doc, y, boxH + 4);

  doc.setFillColor(...COLOR.bgSoft);
  doc.roundedRect(M, y, cw, boxH, 2, 2, 'F');

  gefuellt.forEach((eintrag, i) => {
    const x = M + 5 + (i % 2) * colW;
    const rowY = y + 7 + Math.floor(i / 2) * rowH;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.8);
    setText(doc, COLOR.muted);
    doc.text(eintrag.label.toUpperCase(), x, rowY - 3.2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    setText(doc, COLOR.ink);
    doc.text(kuerzen(doc, eintrag.value, colW - 10), x, rowY + 1);
  });

  return y + boxH + 6;
}

interface Kennzahl {
  label: string;
  wert: string;
  color?: readonly number[];
}

/** Kennzahlen als Kachelreihe, je Zeile bis zu vier Stück. */
function kennzahlen(doc: jsPDF, y: number, werte: Kennzahl[]): number {
  const proZeile = 4;
  const cw = contentWidth(doc);
  const gap = 3;
  const boxW = (cw - gap * (proZeile - 1)) / proZeile;
  const boxH = 14;

  for (let i = 0; i < werte.length; i += proZeile) {
    const zeile = werte.slice(i, i + proZeile);
    y = ensureSpace(doc, y, boxH + 3);

    zeile.forEach((k, j) => {
      const x = M + j * (boxW + gap);

      doc.setFillColor(...COLOR.bgSoft);
      doc.roundedRect(x, y, boxW, boxH, 1.8, 1.8, 'F');

      // Kacheln zeigen meist Zahlen, gelegentlich aber auch einen Status wie
      // „Fehler / Nacharbeit“. Der Wert wird deshalb so weit verkleinert, bis
      // er in die Kachel passt – sonst liefe er in die nächste hinein.
      doc.setFont('helvetica', 'bold');
      let groesse = 13;
      doc.setFontSize(groesse);
      while (groesse > 7.5 && doc.getTextWidth(k.wert) > boxW - 6) {
        groesse -= 0.5;
        doc.setFontSize(groesse);
      }

      setText(doc, k.color ?? COLOR.ink);
      doc.text(kuerzen(doc, k.wert, boxW - 6), x + 3, y + 7.6);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.8);
      setText(doc, COLOR.muted);
      doc.text(kuerzen(doc, k.label.toUpperCase(), boxW - 6), x + 3, y + 11.6);
    });

    y += boxH + gap;
  }

  return y + 2;
}

/** Fortschrittsbalken mit Beschriftung. */
function fortschritt(doc: jsPDF, y: number, prozent: number, label: string): number {
  const cw = contentWidth(doc);
  y = ensureSpace(doc, y, 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setText(doc, COLOR.muted);
  doc.text(label, M, y);
  doc.text(`${prozent} %`, M + cw, y, { align: 'right' });

  const barY = y + 2;
  const anteil = Math.max(0, Math.min(100, prozent)) / 100;

  doc.setFillColor(...COLOR.line);
  doc.roundedRect(M, barY, cw, 2.6, 1.3, 1.3, 'F');

  if (anteil > 0) {
    doc.setFillColor(...COLOR.primary);
    doc.roundedRect(M, barY, cw * anteil, 2.6, 1.3, 1.3, 'F');
  }

  return barY + 7;
}

interface Spalte {
  header: string;
  width: number;
  align?: 'left' | 'right';
}

interface Zelle {
  text: string;
  color?: readonly number[];
  bold?: boolean;
  /**
   * Kleingedruckte Zeile unter dem Zellentext, im Umbruch auf die Breite
   * DIESER Spalte begrenzt.
   *
   * Gedacht fuer Angaben, die zur Spalte gehoeren - etwa die Anlagen-
   * beschreibung unter dem Kennzeichen. Der Unterschied zu `zusatz` ist
   * nicht kosmetisch: `zusatz` laeuft ueber die ganze Tabellenbreite und
   * schiebt sich damit unter die folgenden Spalten. Was zu einer Spalte
   * gehoert, gehoert auch unter diese Spalte.
   */
  unter?: string;
}

interface TabellenZeile {
  zellen: Zelle[];
  /** Zusatzzeilen unter der Zeile, z. B. eine Bemerkung. */
  zusatz?: string[];
}

/** Kopfzeile einer Tabelle. Wird nach jedem Seitenumbruch wiederholt. */
function tableHeader(doc: jsPDF, y: number, spalten: Spalte[]): number {
  const cw = contentWidth(doc);

  doc.setFillColor(...COLOR.primary);
  doc.rect(M, y, cw, 6.4, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.6);
  doc.setTextColor(255, 255, 255);

  let x = M;
  for (const spalte of spalten) {
    const tx = spalte.align === 'right' ? x + spalte.width - 2 : x + 2;
    doc.text(spalte.header, tx, y + 4.3, { align: spalte.align === 'right' ? 'right' : 'left' });
    x += spalte.width;
  }

  return y + 6.4;
}

/**
 * Tabelle mit Zeilenumbruch je Zelle, Zebrastreifen und wiederholter Kopfzeile.
 * Gibt das neue y zurück.
 */
function table(doc: jsPDF, y: number, spalten: Spalte[], zeilen: TabellenZeile[]): number {
  const cw = contentWidth(doc);

  // Alle senkrechten Masse an einer Stelle. Vorher steckten sie verstreut in
  // den Rechnungen, und die erste Zusatzzeile landete dadurch nur 2,1 mm
  // unter der Grundlinie des Zellentexts - bei 8 pt Schrift klebten die
  // beiden Zeilen aneinander, waehrend unter ihnen 4 mm leer blieben.
  const OBEN = 4.2;          // Grundlinie der ersten Textzeile
  const ZEILE = 3.9;         // Zeilenabstand innerhalb einer Zelle
  const UNTER_ZEILE = 3.3;   // Zeilenabstand der Kleinschrift unter einer Zelle
  const ZUSATZ_ZEILE = 3.4;  // Zeilenabstand der Zusatzzeilen ueber die volle Breite
  const LUFT = 3.4;          // Abstand vor der ersten Klein-/Zusatzzeile
  const UNTEN = 2.4;         // Luft unter der letzten Zeile

  y = ensureSpace(doc, y, 20);
  y = tableHeader(doc, y, spalten);

  let zebra = false;

  for (const zeile of zeilen) {
    doc.setFontSize(8);

    // Umbruch je Zelle vorbereiten – die höchste Zelle bestimmt die Zeilenhöhe.
    const umbrochen = zeile.zellen.map((zelle, i) => {
      doc.setFont('helvetica', zelle.bold ? 'bold' : 'normal');
      return doc.splitTextToSize(zelle.text || '', spalten[i].width - 4) as string[];
    });

    // Kleinschrift unter einer Zelle: bricht auf die Breite ihrer eigenen
    // Spalte um und bleibt damit unter ihr stehen.
    doc.setFontSize(7.4);
    doc.setFont('helvetica', 'italic');
    const unterZeilen = zeile.zellen.map((zelle, i) =>
      text(zelle.unter)
        ? doc.splitTextToSize(zelle.unter as string, spalten[i].width - 4) as string[]
        : []
    );

    const zusatzZeilen = (zeile.zusatz ?? [])
      .filter(z => text(z).length > 0)
      .flatMap(z => doc.splitTextToSize(z, cw - 8) as string[]);

    // Grundlinie der jeweils letzten Zeile einer Spalte, relativ zu y.
    const spaltenEnde = umbrochen.map((u, i) => {
      const letzteTextzeile = OBEN + (Math.max(1, u.length) - 1) * ZEILE;
      return unterZeilen[i].length > 0
        ? letzteTextzeile + LUFT + (unterZeilen[i].length - 1) * UNTER_ZEILE
        : letzteTextzeile;
    });

    const letzteGrundlinie = Math.max(OBEN, ...spaltenEnde);
    const zusatzStart = letzteGrundlinie + LUFT;

    const hoehe = (zusatzZeilen.length > 0
      ? zusatzStart + (zusatzZeilen.length - 1) * ZUSATZ_ZEILE
      : letzteGrundlinie) + UNTEN;

    // Passt die Zeile nicht mehr, beginnt die Tabelle auf der neuen Seite mit
    // ihrer Kopfzeile – sonst stünden Zahlen ohne Beschriftung da.
    if (y + hoehe > bottomLimit(doc)) {
      doc.addPage();
      y = tableHeader(doc, TOP_ON_NEW_PAGE, spalten);
      zebra = false;
    }

    if (zebra) {
      doc.setFillColor(...COLOR.zebra);
      doc.rect(M, y, cw, hoehe, 'F');
    }
    zebra = !zebra;

    let x = M;
    umbrochen.forEach((zeilenText, i) => {
      const zelle = zeile.zellen[i];
      const spalte = spalten[i];
      const rechts = spalte.align === 'right';

      doc.setFontSize(8);
      doc.setFont('helvetica', zelle.bold ? 'bold' : 'normal');
      setText(doc, zelle.color ?? COLOR.ink);

      let ty = y + OBEN;
      for (const t of zeilenText) {
        doc.text(t, rechts ? x + spalte.width - 2 : x + 2, ty, {
          align: rechts ? 'right' : 'left'
        });
        ty += ZEILE;
      }

      if (unterZeilen[i].length > 0) {
        doc.setFontSize(7.4);
        doc.setFont('helvetica', 'italic');
        setText(doc, COLOR.muted);

        let uy = y + OBEN + (Math.max(1, zeilenText.length) - 1) * ZEILE + LUFT;
        for (const t of unterZeilen[i]) {
          doc.text(t, rechts ? x + spalte.width - 2 : x + 2, uy, {
            align: rechts ? 'right' : 'left'
          });
          uy += UNTER_ZEILE;
        }
      }

      x += spalte.width;
    });

    if (zusatzZeilen.length > 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7.4);
      setText(doc, COLOR.muted);

      let ty = y + zusatzStart;
      for (const t of zusatzZeilen) {
        doc.text(t, M + 4, ty);
        ty += ZUSATZ_ZEILE;
      }
    }

    doc.setDrawColor(...COLOR.line);
    doc.setLineWidth(0.2);
    doc.line(M, y + hoehe, M + cw, y + hoehe);

    y += hoehe;
  }

  return y + 4;
}

/** Hinweiszeile, wenn ein Abschnitt keine Daten hat. */
function leerHinweis(doc: jsPDF, y: number, hinweis: string): number {
  y = paragraph(doc, y + 1, hinweis, { size: 9, color: COLOR.muted, style: 'italic' });
  return y + 3;
}

// ── Farbgebung der Ergebnisse ───────────────────────────────────────────────

/**
 * Enums kommen als Name vom Server, ältere Antworten lieferten Zahlen.
 * Der Vergleich deckt beides ab.
 */
function istWert(wert: unknown, name: string, zahl: number): boolean {
  return wert === name || wert === zahl;
}

function pruefergebnisFarbe(wert: unknown): readonly number[] {
  if (istWert(wert, 'Bestanden', 1)) return COLOR.ok;
  if (istWert(wert, 'NichtBestanden', 2)) return COLOR.notOk;
  return COLOR.muted;
}

function pruefpunktFarbe(wert: unknown): readonly number[] {
  if (istWert(wert, 'Bestanden', 1)) return COLOR.ok;
  if (istWert(wert, 'NichtBestanden', 2)) return COLOR.notOk;
  if (istWert(wert, 'Irrelevant', 3)) return COLOR.faint;
  return COLOR.open;
}

function signalStatusFarbe(wert: unknown): readonly number[] {
  if (istWert(wert, 'Freigegeben', 3)) return COLOR.ok;
  if (istWert(wert, 'FehlerNacharbeit', 4)) return COLOR.notOk;
  if (istWert(wert, 'Offen', 0)) return COLOR.muted;
  return COLOR.open;
}

// ── Abschnitte ──────────────────────────────────────────────────────────────

/**
 * Allgemeine Informationen im Kopf des Protokolls.
 *
 * `tiefe` sagt, wie weit hinunter der Kopf reicht. Normalerweise ist das der
 * Umfang des Protokolls selbst. Das Sammel-PDF mehrerer Messstellen setzt sie
 * bewusst auf 'Inbetriebnahme': dort steht der Kopf einmal am Anfang und darf
 * deshalb keine Angaben einer einzelnen Messstelle enthalten.
 */
function allgemeinerKopf(
  doc: jsPDF,
  y: number,
  protokoll: IbnProtokoll,
  tiefe: IbnProtokollUmfang = protokoll.umfang
): number {
  const k = protokoll.kopf;

  const eintraege: InfoEintrag[] = [
    { label: 'Projekt', value: text(k.projektName) },
    { label: 'Projektnummer', value: text(k.projektNummer) },
    { label: 'Kunde', value: text(k.kundeName) },
    {
      label: 'Anlage',
      value: [text(k.anlageName), text(k.anlageOrt)].filter(t => t).join(' · ')
    },
    {
      label: 'Projekt angelegt',
      value: [datum(k.projektAngelegtAmUtc), text(k.projektAngelegtVon)]
        .filter(t => t).join(' · ')
    }
  ];

  if (tiefe !== 'Projekt') {
    eintraege.push(
      { label: 'Inbetriebnahme', value: text(k.inbetriebnahmeName) },
      { label: 'Quelldatei', value: text(k.quellDatei) },
    
      {
        label: 'Importiert',
        value: [datumZeit(k.importiertAmUtc), text(k.importiertVon)]
          .filter(t => t).join(' · ')
      }
    );
  }

  if (tiefe === 'Messstelle') {
    eintraege.push(
      { label: 'Messstelle', value: text(k.messstelleBezeichnung) },
      { label: 'Kategorie', value: text(k.messstelleKategorie) || 'noch nicht zugeordnet' },
      
    );
  }

  eintraege.push({
    label: 'Protokoll erstellt',
    value: [datumZeit(protokoll.erstelltAmUtc), text(protokoll.erstelltVon)]
      .filter(t => t).join(' · ')
  });

  y = sectionTitle(doc, y, 'Allgemeine Informationen');
  return infoGrid(doc, y, eintraege);
}

/**
 * Kennzahlen eines Umfangs. Die drei Blöcke – Bearbeitungsstand, Workflow-
 * Status und fachliches Gesamtergebnis – stehen bewusst getrennt: sie messen
 * Unterschiedliches und dürfen nicht miteinander verrechnet werden.
 */
function statistikAbschnitt(
  doc: jsPDF,
  y: number,
  statistik: IbnProtokollStatistik,
  titel: string
): number {
  y = sectionTitle(doc, y, titel);

  y = kennzahlen(doc, y, [
    { label: 'Messstellen gesamt', wert: String(statistik.messstellenGesamt) },
    { label: 'Gestartet', wert: String(statistik.messstellenGestartet) },
    { label: 'Vollständig geprüft', wert: String(statistik.messstellenAbgeschlossen) },
    { label: 'Nicht gestartet', wert: String(statistik.messstellenNichtGestartet), color: COLOR.muted }
  ]);

  y = fortschritt(doc, y, statistik.fortschrittProzent, 'Gesamtfortschritt');

  if (statistik.messstellenGesamt === 0) {
    return leerHinweis(doc, y, 'Diesem Umfang sind noch keine Messstellen zugeordnet.');
  }

  y = subTitle(doc, y, 'Messstellen nach Prüfergebnis');
  y = kennzahlen(doc, y, [
    { label: 'Bestanden', wert: String(statistik.ergebnisBestanden), color: COLOR.ok },
    { label: 'Nicht bestanden', wert: String(statistik.ergebnisNichtBestanden), color: COLOR.notOk },
    { label: 'Noch nicht geprüft', wert: String(statistik.ergebnisNichtGeprueft), color: COLOR.muted },
    { label: 'Kategorisiert', wert: `${statistik.messstellenMitKategorie} / ${statistik.messstellenGesamt}` }
  ]);

  y = subTitle(doc, y, 'Messstellen nach Status');
  y = kennzahlen(doc, y, [
    { label: 'Offen', wert: String(statistik.statusOffen), color: COLOR.muted },
    { label: 'In Bearbeitung', wert: String(statistik.statusInBearbeitung), color: COLOR.open },
    { label: 'Getestet', wert: String(statistik.statusGetestet), color: COLOR.open },
    { label: 'Freigegeben', wert: String(statistik.statusFreigegeben), color: COLOR.ok },
    { label: 'Fehler / Nacharbeit', wert: String(statistik.statusFehlerNacharbeit), color: COLOR.notOk }
  ]);

  y = subTitle(doc, y, 'Prüfpunkte');
  y = kennzahlen(doc, y, [
    { label: 'Gesamt', wert: String(statistik.pruefpunkteGesamt) },
    { label: 'Bestanden', wert: String(statistik.pruefpunkteBestanden), color: COLOR.ok },
    { label: 'Nicht bestanden', wert: String(statistik.pruefpunkteNichtBestanden), color: COLOR.notOk },
    { label: 'Irrelevant', wert: String(statistik.pruefpunkteIrrelevant), color: COLOR.faint },
    { label: 'Noch offen', wert: String(statistik.pruefpunkteNichtGeprueft), color: COLOR.open },
    { label: 'Abgearbeitet', wert: `${statistik.pruefpunkteErledigt} / ${statistik.pruefpunkteGesamt}` }
  ]);

  return y;
}

/** Tabelle aller Messstellen einer Inbetriebnahme. */
function messstellenTabelle(
  doc: jsPDF,
  y: number,
  messstellen: IbnProtokollMessstelleZeile[]
): number {
  if (messstellen.length === 0) {
    return leerHinweis(doc, y, 'Dieser Inbetriebnahme sind noch keine Messstellen zugeordnet.');
  }

  const spalten: Spalte[] = [
    { header: 'Zeile', width: 13 },
    { header: 'Bezeichnung', width: 47 },
    { header: 'Kategorie', width: 30 },
    { header: 'Status', width: 27 },
    { header: 'Ergebnis', width: 29 },
    { header: 'Prüfpunkte', width: 22, align: 'right' },
    { header: '%', width: 14, align: 'right' }
  ];

  const zeilen: TabellenZeile[] = messstellen.map(m => ({
    zellen: [
      { text: String(m.rowNumber), color: COLOR.muted },
      // Die Beschreibung gehoert zur Bezeichnung und steht deshalb unter ihr,
      // nicht als Zeile ueber die ganze Tabellenbreite. Sonst lief sie unter
      // Kategorie und Status hindurch und las sich wie deren Fussnote.
      { text: m.bezeichnung, bold: true, unter: text(m.beschreibung) },
      { text: text(m.kategorie) || '–', color: text(m.kategorie) ? COLOR.ink : COLOR.faint },
      { text: m.signalStatusText, color: signalStatusFarbe(m.signalStatus) },
      { text: m.pruefergebnisText, color: pruefergebnisFarbe(m.pruefergebnis) },
      { text: `${m.pruefpunkteErledigt} / ${m.pruefpunkteGesamt}` },
      { text: `${m.fortschrittProzent}` }
    ],
    zusatz: [
      text(m.pruefergebnisBemerkung) ? `Bemerkung: ${text(m.pruefergebnisBemerkung)}` : '',
      m.pruefpunkteNichtBestanden > 0
        ? `${m.pruefpunkteNichtBestanden} Prüfpunkt${m.pruefpunkteNichtBestanden === 1 ? '' : 'e'} nicht bestanden`
        : ''
    ]
  }));

  return table(doc, y, spalten, zeilen);
}

/** Eine Inbetriebnahme mit Kopfzeile, Kurzkennzahlen und Messstellentabelle. */
function inbetriebnahmeAbschnitt(
  doc: jsPDF,
  y: number,
  inbetriebnahme: IbnProtokollInbetriebnahme,
  mitKennzahlen: boolean
): number {
  const s = inbetriebnahme.statistik;

  y = subTitle(
    doc,
    y,
    inbetriebnahme.name,
    `${s.messstellenGesamt} Messstellen · ${s.fortschrittProzent} % · importiert ${datum(inbetriebnahme.importiertAmUtc)}`
  );

  if (mitKennzahlen) {
    y = kennzahlen(doc, y, [
      { label: 'Gestartet', wert: `${s.messstellenGestartet} / ${s.messstellenGesamt}` },
      { label: 'Vollständig geprüft', wert: `${s.messstellenAbgeschlossen} / ${s.messstellenGesamt}` },
      { label: 'Bestanden', wert: String(s.ergebnisBestanden), color: COLOR.ok },
      { label: 'Nicht bestanden', wert: String(s.ergebnisNichtBestanden), color: COLOR.notOk }
    ]);
  }

  return messstellenTabelle(doc, y, inbetriebnahme.messstellen);
}

/** Allgemeine Daten einer Messstelle als zweispaltiges Raster. */
function allgemeineDaten(doc: jsPDF, y: number, felder: IbnProtokollFeld[]): number {
  y = sectionTitle(doc, y, 'Allgemeine Daten der Messstelle');

  if (felder.length === 0) {
    return leerHinweis(doc, y, 'Für diese Messstelle sind keine Werte hinterlegt.');
  }

  return infoGrid(doc, y, felder.map(f => ({ label: f.label, value: f.wert })));
}

/** Prüfpunkte, nach Abschnitt gruppiert – wie auf der Detailseite. */
function pruefpunkteAbschnitt(
  doc: jsPDF,
  y: number,
  punkte: IbnProtokollPruefpunkt[],
  detail: IbnProtokollMessstelleDetail
): number {
  y = sectionTitle(
    doc,
    y,
    'Prüfpunkte',
    `${detail.pruefpunkteErledigt} von ${detail.pruefpunkteGesamt} abgearbeitet`
  );

  if (punkte.length === 0) {
    return leerHinweis(
      doc,
      y,
      'Für diese Messstelle sind noch keine Prüfpunkte vorhanden – sie gilt damit als nicht gestartet.'
    );
  }

  const spalten: Spalte[] = [
    { header: 'Prüfpunkt', width: 88 },
    { header: 'Ergebnis', width: 30 },
    { header: 'Quittiert', width: 64 }
  ];

  // Reihenfolge der Abschnitte so lassen, wie sie vom Server kommt.
  const abschnitte: string[] = [];
  for (const punkt of punkte) {
    if (!abschnitte.includes(punkt.abschnitt)) {
      abschnitte.push(punkt.abschnitt);
    }
  }

  for (const abschnitt of abschnitte) {
    const eigene = punkte.filter(p => p.abschnitt === abschnitt);
    const erledigt = eigene.filter(p => !istWert(p.ergebnis, 'NichtGeprueft', 0)).length;

    y = subTitle(doc, y, abschnitt, `${erledigt} / ${eigene.length} abgearbeitet`);

    y = table(doc, y, spalten, eigene.map(p => ({
      zellen: [
        { text: p.bezeichnung },
        {
          text: p.ergebnisText,
          color: pruefpunktFarbe(p.ergebnis),
          bold: istWert(p.ergebnis, 'NichtBestanden', 2)
        },
        {
          text: [datumZeit(p.quittiertAmUtc), text(p.quittiertVon)].filter(t => t).join(' · ') || '–',
          color: COLOR.muted
        }
      ],
      zusatz: [text(p.bemerkung) ? `Bemerkung: ${text(p.bemerkung)}` : '']
    })));
  }

  return y;
}

/**
 * Eine Messstelle in voller Tiefe: Prüfstand, allgemeine Daten, Prüfpunkte
 * und Prüfverlauf.
 *
 * Genau dieser Baustein steckt im Einzel-PDF, in jeder Messstelle des
 * Sammel-PDFs und in jedem PDF einer ZIP-Datei. Ein zweites Layout für
 * dieselbe Sache gibt es bewusst nicht – sonst laufen die Varianten
 * über kurz oder lang auseinander.
 */
function messstelleDetailAbschnitt(
  doc: jsPDF,
  y: number,
  messstelle: IbnProtokollMessstelleDetail
): number {
  y = sectionTitle(doc, y, 'Prüfstand der Messstelle');
  y = kennzahlen(doc, y, [
    { label: 'Status', wert: messstelle.signalStatusText, color: signalStatusFarbe(messstelle.signalStatus) },
    { label: 'Gesamtergebnis', wert: messstelle.pruefergebnisText, color: pruefergebnisFarbe(messstelle.pruefergebnis) },
    { label: 'Prüfpunkte', wert: `${messstelle.pruefpunkteErledigt} / ${messstelle.pruefpunkteGesamt}` },
    { label: 'Fotos', wert: String(messstelle.bilderCount) }
  ]);
  y = fortschritt(doc, y, messstelle.fortschrittProzent, 'Fortschritt der Prüfpunkte');

  if (text(messstelle.pruefergebnisBemerkung)) {
    y = paragraph(doc, y, `Bemerkung zum Gesamtergebnis: ${text(messstelle.pruefergebnisBemerkung)}`, {
      size: 9,
      color: pruefergebnisFarbe(messstelle.pruefergebnis)
    });
    y += 3;
  }

  y = allgemeineDaten(doc, y, messstelle.allgemeineDaten);
  y = pruefpunkteAbschnitt(doc, y, messstelle.pruefpunkte, messstelle);
  return pruefverlaufAbschnitt(doc, y, messstelle.pruefverlauf);
}

/**
 * Der vollständige Prüfverlauf: wer wann was geändert hat.
 *
 * Titel, Betreff, Werte, Zusatz und Bemerkung kommen aus `formatAktivitaet` –
 * demselben Formatter wie der Aktivitätsverlauf auf der Detailseite. Hier
 * wird nur angeordnet, nichts neu interpretiert.
 */
function pruefverlaufAbschnitt(
  doc: jsPDF,
  y: number,
  verlauf: MessstellePruefprotokoll[]
): number {
  y = sectionTitle(
    doc,
    y,
    'Prüfverlauf',
    verlauf.length > 0 ? `${verlauf.length} Einträge, ältester zuerst` : undefined
  );

  if (verlauf.length === 0) {
    return leerHinweis(doc, y, 'Zu dieser Messstelle wurde noch keine Änderung erfasst.');
  }

  const spalten: Spalte[] = [
    { header: 'Zeitpunkt', width: 30 },
    { header: 'Benutzer', width: 34 },
    { header: 'Aktivität', width: 52 },
    { header: 'Details', width: 66 }
  ];

  const zeilen: TabellenZeile[] = verlauf.map(e => {
    const a = formatAktivitaet(e);

    /*
     * Normalerweise zeigt das PDF nur den NEUEN Zustand.
     *
     * Ausnahme:
     * Bei "Allgemeine Daten geändert" soll nachvollziehbar sein,
     * welcher konkrete Wert geändert wurde:
     *
     * Messbereich Max
     * 5 → 10
     */
    let wertAnzeige = a.neuerWert;

    if (a.titel === 'Allgemeine Daten geändert') {
      const vorher = text(a.vorherigerWert) || '(leer)';
      const nachher = text(a.neuerWert) || '(leer)';

      wertAnzeige = `${vorher} -> ${nachher}`;
    }

    const haupt = [
      a.betreff,
      wertAnzeige
    ].filter((t): t is string => !!t);

    const neben = [
      a.zusatz,
      a.bemerkung ? `„${a.bemerkung}“` : null
    ].filter((t): t is string => !!t);

    return {
      zellen: [
        {
          text: datumZeit(e.zeitpunktUtc),
          color: COLOR.muted
        },
        {
          text: text(e.benutzerName) || '–'
        },
        {
          text: a.titel,
          bold: true
        },
        haupt.length > 0
          ? {
            text: haupt.join('\n'),
            unter: neben.join('\n')
          }
          : {
            text: neben.join('\n') || '–',
            color: neben.length ? COLOR.muted : COLOR.faint
          }
      ]
    };
  });

  return table(doc, y, spalten, zeilen);
}

/** Fußzeile auf allen Seiten – nachträglich, damit die Seitenzahl stimmt. */
function drawFooters(doc: jsPDF, protokoll: IbnProtokoll): void {
  const seiten = doc.getNumberOfPages();
  const links = `${DOKUMENT_TITEL} · ${protokoll.titel} · erstellt am ${datum(protokoll.erstelltAmUtc)}`;

  for (let i = 1; i <= seiten; i++) {
    doc.setPage(i);
    const pw = pageWidth(doc);
    const ph = pageHeight(doc);

    doc.setDrawColor(...COLOR.line);
    doc.setLineWidth(0.25);
    doc.line(M, ph - 11, pw - M, ph - 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    setText(doc, COLOR.faint);
    doc.text(kuerzen(doc, links, pw - 2 * M - 30), M, ph - 7);
    doc.text(`Seite ${i} von ${seiten}`, pw - M, ph - 7, { align: 'right' });
  }
}

// ── Öffentliche API ─────────────────────────────────────────────────────────

/** Untertitel im Kopf – sagt in einer Zeile, worüber das Protokoll geht. */
function subline(protokoll: IbnProtokoll): string {
  const umfang = protokoll.umfang === 'Projekt'
    ? 'Projekt'
    : protokoll.umfang === 'Inbetriebnahme'
      ? 'Inbetriebnahme'
      : 'Messstelle';

  return `${umfang}: ${protokoll.titel} · ${datum(protokoll.erstelltAmUtc)}`;
}

/** Baut das Protokoll und liefert das jsPDF-Dokument zurück. */
export async function buildIbnProtokollPdf(protokoll: IbnProtokoll): Promise<jsPDF> {
  const doc = new jsPDF();

  let y = await drawPdfHeader(doc, DOKUMENT_TITEL, subline(protokoll));

  y = allgemeinerKopf(doc, y, protokoll);

  if (protokoll.umfang === 'Messstelle' && protokoll.messstelle) {
    messstelleDetailAbschnitt(doc, y, protokoll.messstelle);

    drawFooters(doc, protokoll);
    return doc;
  }

  if (protokoll.statistik) {
    y = statistikAbschnitt(
      doc,
      y,
      protokoll.statistik,
      protokoll.umfang === 'Projekt' ? 'Gesamtstatus des Projekts' : 'Status der Inbetriebnahme'
    );
  }

  if (protokoll.umfang === 'Projekt') {
    y = sectionTitle(
      doc,
      y,
      'Inbetriebnahmen',
      `${protokoll.inbetriebnahmen.length} Inbetriebnahme${protokoll.inbetriebnahmen.length === 1 ? '' : 'n'}`
    );

    if (protokoll.inbetriebnahmen.length === 0) {
      leerHinweis(doc, y, 'Diesem Projekt ist noch keine Inbetriebnahme zugeordnet.');
    } else {
      for (const inbetriebnahme of protokoll.inbetriebnahmen) {
        y = inbetriebnahmeAbschnitt(doc, y, inbetriebnahme, true);
      }
    }
  } else {
    y = sectionTitle(doc, y, 'Messstellen');
    for (const inbetriebnahme of protokoll.inbetriebnahmen) {
      y = messstellenTabelle(doc, y, inbetriebnahme.messstellen);
    }
  }

  drawFooters(doc, protokoll);
  return doc;
}

/**
 * Baut das Protokoll und startet den Download. Der Dateiname kommt vom Server
 * (`[Name]_IBN_Protokoll_[Jahr_Monat_Tag]`), damit Datum und Zeitzone überall
 * gleich sind.
 */
export async function downloadIbnProtokollPdf(protokoll: IbnProtokoll): Promise<void> {
  const doc = await buildIbnProtokollPdf(protokoll);
  doc.save(`${protokoll.dateiName}.pdf`);
}

/** Baut das Protokoll und liefert es als PDF-Bytes – Grundlage der ZIP-Datei. */
export async function buildIbnProtokollPdfBytes(protokoll: IbnProtokoll): Promise<Uint8Array> {
  const doc = await buildIbnProtokollPdf(protokoll);
  return new Uint8Array(doc.output('arraybuffer') as ArrayBuffer);
}

// ── Sammelprotokoll mehrerer Messstellen ────────────────────────────────────

/** Was ein Sammel-PDF über mehrere Messstellen zusammenhält. */
export interface IbnProtokollSammel {
  /** Titel des Sammelprotokolls – üblicherweise der Name der Inbetriebnahme. */
  titel: string;

  /** Dateiname ohne Endung. */
  dateiName: string;

  /**
   * Die Einzelprotokolle der gewählten Messstellen, in der Reihenfolge der
   * Exportliste. Jedes stammt aus demselben Endpunkt wie beim Einzel-Export,
   * trägt also exakt dieselben Daten.
   */
  protokolle: IbnProtokoll[];
}

/**
 * Kopfzeile einer Messstelle im Sammel-PDF.
 *
 * Ersetzt den Kopf, den ein Einzel-PDF ganz oben hätte: Projekt- und
 * IBN-Daten stehen im Sammel-PDF nur einmal am Anfang, die Messstelle
 * selbst muss aber weiterhin benannt sein.
 */
function messstelleKopfImSammelPdf(
  doc: jsPDF,
  y: number,
  protokoll: IbnProtokoll,
  messstelle: IbnProtokollMessstelleDetail,
  nummer: number,
  gesamt: number
): number {
  y = sectionTitle(
    doc,
    y,
    messstelle.bezeichnung,
    `Messstelle ${nummer} von ${gesamt}`
  );

  return infoGrid(doc, y, [
    { label: 'Messstelle', value: text(protokoll.kopf.messstelleBezeichnung) || messstelle.bezeichnung },
    { label: 'Beschreibung', value: text(messstelle.beschreibung) },
    { label: 'Kategorie', value: text(messstelle.kategorie) || 'noch nicht zugeordnet' },
    { label: 'Zeile in der Datei', value: String(messstelle.rowNumber) },
    { label: 'Inbetriebnahme', value: text(messstelle.inbetriebnahmeName) }
  ]);
}

/**
 * Ein PDF über mehrere Messstellen.
 *
 * Aufbau: Kopf und Überblick des Projekts bzw. der Inbetriebnahme genau
 * einmal am Anfang, danach jede Messstelle auf einer neuen Seite – jeweils
 * mit demselben Detailteil wie im Einzel-PDF.
 */
export async function buildIbnProtokollSammelPdf(sammel: IbnProtokollSammel): Promise<jsPDF> {
  // Protokoll und Detail bleiben paarweise beisammen: ein Protokoll ohne
  // Messstellenteil würde die Nummerierung sonst verschieben.
  const eintraege = sammel.protokolle
    .map(protokoll => ({ protokoll, messstelle: protokoll.messstelle }))
    .filter((e): e is { protokoll: IbnProtokoll; messstelle: IbnProtokollMessstelleDetail } => !!e.messstelle);

  if (eintraege.length === 0) {
    throw new Error('Für das Sammelprotokoll wurde keine Messstelle geliefert.');
  }

  const details = eintraege.map(e => e.messstelle);

  // Der erste Eintrag liefert Projekt-, IBN- und Zeitstempeldaten. Die sind
  // für alle gewählten Messstellen dieselben, weil die Auswahl innerhalb
  // EINER Inbetriebnahme stattfindet.
  const erstes = eintraege[0].protokoll;

  const rahmen: IbnProtokoll = {
    ...erstes,
    umfang: 'Inbetriebnahme',
    titel: sammel.titel,
    dateiName: sammel.dateiName,
    messstelle: null
  };

  const doc = new jsPDF();

  let y = await drawPdfHeader(doc, DOKUMENT_TITEL, subline(rahmen));

  // Projekt-/IBN-Daten nur EINMAL – deshalb die Tiefe 'Inbetriebnahme'.
  y = allgemeinerKopf(doc, y, rahmen, 'Inbetriebnahme');

  y = sectionTitle(
    doc,
    y,
    'Ausgewählte Messstellen',
    `${details.length} Messstelle${details.length === 1 ? '' : 'n'}`
  );

  y = messstellenTabelle(doc, y, details);

  eintraege.forEach((eintrag, index) => {
    // Jede Messstelle beginnt auf einer neuen Seite.
    doc.addPage();

    const seitenY = messstelleKopfImSammelPdf(
      doc,
      TOP_ON_NEW_PAGE,
      eintrag.protokoll,
      eintrag.messstelle,
      index + 1,
      eintraege.length
    );

    messstelleDetailAbschnitt(doc, seitenY, eintrag.messstelle);
  });

  drawFooters(doc, rahmen);
  return doc;
}

/** Baut das Sammelprotokoll und startet den Download. */
export async function downloadIbnProtokollSammelPdf(sammel: IbnProtokollSammel): Promise<void> {
  const doc = await buildIbnProtokollSammelPdf(sammel);
  doc.save(`${sammel.dateiName}.pdf`);
}
